import { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useGetCurrentBranchWarehousesQuery } from '@app/api/Warehouses';
import { FRONT_WARNING, IMPORT_PRODUCTS_PATH, WARNING } from '@app/constants';
import { useUserPrint } from '@app/hooks';
import { getWarehouseIdFromSearchParams } from '@app/pages/Warehouses/Products/utils';
import { FileContext } from '@app/providers';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';

import type { ChangeEvent } from 'react';

const EXCEL_EXTENSIONS = ['xls', 'xlsx'];

const readFileAsBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      if (typeof reader.result !== 'string') {
        reject(new Error('Не удалось прочитать файл'));
        return;
      }

      resolve(reader.result.replace('data:', '').replace(/^.+,/, ''));
    };
    reader.onerror = () => {
      reject(reader.error || new Error('Не удалось прочитать файл'));
    };
    reader.readAsDataURL(file);
  });

export const useProductsImport = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setFile, setExtension } = useContext(FileContext);
  const { showNotification } = useNotification();
  const {
    params: { ActiveBranchId },
  } = useUserPrint();
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [isWarehouseModalOpen, setIsWarehouseModalOpen] = useState(false);
  const [selectedWarehouseId, setSelectedWarehouseId] = useState<number | null>(
    null
  );

  const { data: warehouses, isFetching: isWarehousesFetching } =
    useGetCurrentBranchWarehousesQuery(
      { ActiveBranchId },
      { skip: !ActiveBranchId || !isWarehouseModalOpen }
    );

  const warehousesOptions = useMemo(
    () => warehouses?.map(({ Id, Name }) => ({ label: Name, value: Id })) || [],
    [warehouses]
  );

  const openWarehouseModal = useCallback(() => {
    setSelectedWarehouseId(getWarehouseIdFromSearchParams(searchParams));
    setIsWarehouseModalOpen(true);
  }, [searchParams]);

  const closeWarehouseModal = useCallback(() => {
    setIsWarehouseModalOpen(false);
    setSelectedWarehouseId(null);
  }, []);

  const confirmWarehouseModal = useCallback(() => {
    if (!selectedWarehouseId) {
      showNotification('Пожалуйста, выберите склад для импорта', FRONT_WARNING);
      return;
    }

    setIsWarehouseModalOpen(false);
    inputRef.current?.click();
  }, [selectedWarehouseId, showNotification]);

  const handleFileChange = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const input = event.target;
      const file = input.files?.[0];
      const filename = file?.name || '';
      const extension = filename.split('.').pop()?.toLowerCase() || '';
      const warehouseId = selectedWarehouseId;

      if (!file) {
        showNotification('Файл не найден', FRONT_WARNING);
        input.value = '';
        return;
      }

      if (!EXCEL_EXTENSIONS.includes(extension)) {
        showNotification('Неизвестное расширение файла', FRONT_WARNING);
        input.value = '';
        return;
      }

      if (!warehouseId) {
        showNotification(
          'Пожалуйста, выберите склад для импорта',
          FRONT_WARNING
        );
        input.value = '';
        return;
      }

      try {
        const data = await readFileAsBase64(file);

        setFile(data);
        setExtension(extension);

        // Импорт товаров выполняется в фоновом режиме: уходим на отдельную
        // страницу с live-прогрессом через SignalR (по аналогии с услугами).
        navigate(IMPORT_PRODUCTS_PATH, {
          state: { file: data, extension, warehouseId },
        });
      } catch (error) {
        showNotification(error, WARNING);
      } finally {
        input.value = '';
        setSelectedWarehouseId(null);
      }
    },
    [navigate, selectedWarehouseId, setExtension, setFile, showNotification]
  );

  return {
    inputRef,
    openWarehouseModal,
    isWarehouseModalOpen,
    closeWarehouseModal,
    confirmWarehouseModal,
    handleFileChange,
    warehousesOptions,
    isWarehousesFetching,
    selectedWarehouseId,
    setSelectedWarehouseId,
  };
};
